// Analytics and Reports page

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Avatar,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  DateRange as DateRangeIcon,
  Print as PrintIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';

// Mock analytics data
const generateAnalyticsData = () => {
  const months = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];
  const currentMonth = new Date().getMonth();
  
  const monthlySpending = months.map((month, index) => ({
    month,
    spending: Math.floor(Math.random() * 3000) + 1000,
    orders: Math.floor(Math.random() * 15) + 5,
    trend: index <= currentMonth ? Math.random() > 0.5 ? 'up' : 'down' : null
  }));

  const topCategories = [
    { name: 'Papier i materiały piśmienne', spending: 8750, percentage: 35, orders: 45 },
    { name: 'Tonery i tusze', spending: 6200, percentage: 25, orders: 28 },
    { name: 'Sprzęt biurowy', spending: 4900, percentage: 20, orders: 22 },
    { name: 'Środki czystości', spending: 3100, percentage: 12, orders: 18 },
    { name: 'Pozostałe', spending: 2050, percentage: 8, orders: 12 }
  ];

  const topProducts = [
    { name: 'Papier A4 Premium 500k', orders: 24, spending: 2400, trend: 'up' },
    { name: 'Toner HP LaserJet', orders: 18, spending: 3240, trend: 'up' },
    { name: 'Długopisy BIC Cristal', orders: 15, spending: 450, trend: 'down' },
    { name: 'Segregatory A4', orders: 12, spending: 960, trend: 'up' },
    { name: 'Kartridge Canon', orders: 10, spending: 1800, trend: 'down' }
  ];

  const budgetData = {
    monthly: 10000,
    spent: 7350,
    remaining: 2650,
    projection: 9200,
    alerts: [
      { type: 'warning', message: 'Budżet wykorzystany w 73%' },
      { type: 'info', message: 'Prognoza przekroczenia o 200 zł' }
    ]
  };

  const savings = {
    total: 1240,
    bulkDiscounts: 680,
    loyaltyProgram: 320,
    promotions: 240
  };

  return {
    monthlySpending,
    topCategories,
    topProducts,
    budgetData,
    savings
  };
};

const analyticsData = generateAnalyticsData();

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState('last12months');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const budgetPercentage = (analyticsData.budgetData.spent / analyticsData.budgetData.monthly) * 100;

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold">
            Raporty i Analityka
          </Typography>
          <Box display="flex" gap={1}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Okres</InputLabel>
              <Select
                value={timeRange}
                label="Okres"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="last30days">Ostatnie 30 dni</MenuItem>
                <MenuItem value="last3months">Ostatnie 3 miesiące</MenuItem>
                <MenuItem value="last6months">Ostatnie 6 miesięcy</MenuItem>
                <MenuItem value="last12months">Ostatnie 12 miesięcy</MenuItem>
                <MenuItem value="custom">Niestandardowy</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<DownloadIcon />}>
              Eksport PDF
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Szczegółowa analiza wydatków i prognoza kosztów biurowych
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <MoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {analyticsData.budgetData.spent.toLocaleString()} zł
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Wydatki w tym miesiącu
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="success.main">
                    +{analyticsData.savings.total.toLocaleString()} zł
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Zaoszczędzone w tym roku
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: budgetPercentage > 80 ? 'warning.main' : 'info.main' }}>
                  <AssessmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" color={budgetPercentage > 80 ? 'warning.main' : 'info.main'}>
                    {budgetPercentage.toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Budżetu wykorzystane
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <DateRangeIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="error.main">
                    {analyticsData.budgetData.projection.toLocaleString()} zł
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Prognoza na koniec miesiąca
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Budget Alert */}
      {analyticsData.budgetData.alerts.map((alert, index) => (
        <Alert 
          key={index}
          severity={alert.type as any} 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small">
              Szczegóły
            </Button>
          }
        >
          {alert.message}
        </Alert>
      ))}

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="analytics tabs">
          <Tab label="Wydatki miesięczne" />
          <Tab label="Kategorie" />
          <Tab label="Produkty" />
          <Tab label="Budżet" />
          <Tab label="Oszczędności" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {/* Monthly Spending Chart */}
          <Typography variant="h6" gutterBottom>
            Wydatki w ostatnich 12 miesiącach
          </Typography>
          
          <Grid container spacing={2}>
            {analyticsData.monthlySpending.map((data, index) => {
              const maxSpending = Math.max(...analyticsData.monthlySpending.map(d => d.spending));
              const heightPercentage = (data.spending / maxSpending) * 100;
              
              return (
                <Grid item xs={2} key={data.month}>
                  <Box textAlign="center">
                    <Box 
                      sx={{ 
                        height: 200, 
                        display: 'flex', 
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                        mb: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: `${heightPercentage}%`,
                          bgcolor: index <= new Date().getMonth() ? 'primary.main' : 'grey.300',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          pb: 1
                        }}
                      >
                        {data.trend && (
                          <Box sx={{ color: 'white', fontSize: '0.7rem' }}>
                            {data.trend === 'up' ? <TrendingUpIcon fontSize="inherit" /> : <TrendingDownIcon fontSize="inherit" />}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="caption" display="block">
                      {data.month}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {data.spending.toLocaleString()} zł
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.orders} zamówień
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Categories */}
          <Typography variant="h6" gutterBottom>
            Wydatki wg kategorii
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Kategoria</TableCell>
                      <TableCell align="right">Zamówienia</TableCell>
                      <TableCell align="right">Wydatki</TableCell>
                      <TableCell align="right">Udział</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.topCategories.map((category) => (
                      <TableRow key={category.name}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {category.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{category.orders}</TableCell>
                        <TableCell align="right">
                          {category.spending.toLocaleString()} zł
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            <LinearProgress
                              variant="determinate"
                              value={category.percentage}
                              sx={{ width: 60, height: 6 }}
                            />
                            <Typography variant="body2">
                              {category.percentage}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Rozkład wydatków
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'inline-flex', width: '100%', justifyContent: 'center' }}>
                    {/* Simple visual representation */}
                    <Box sx={{ width: 200, height: 200, borderRadius: '50%', background: 'conic-gradient(#1976d2 0% 35%, #2196f3 35% 60%, #42a5f5 60% 80%, #64b5f6 80% 92%, #90caf9 92% 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ width: 100, height: 100, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                        <Typography variant="h6">25k zł</Typography>
                        <Typography variant="caption">Łącznie</Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Top Products */}
          <Typography variant="h6" gutterBottom>
            Najczęściej zamawiany produkty
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Produkt</TableCell>
                  <TableCell align="right">Zamówienia</TableCell>
                  <TableCell align="right">Wydatki</TableCell>
                  <TableCell align="right">Trend</TableCell>
                  <TableCell align="center">Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyticsData.topProducts.map((product, index) => (
                  <TableRow key={product.name}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                          {index + 1}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {product.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{product.orders}</TableCell>
                    <TableCell align="right">
                      {product.spending.toLocaleString()} zł
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        icon={product.trend === 'up' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        label={product.trend === 'up' ? 'Wzrost' : 'Spadek'}
                        color={product.trend === 'up' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Szczegóły produktu">
                        <IconButton size="small">
                          <AssessmentIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {/* Budget Analysis */}
          <Typography variant="h6" gutterBottom>
            Analiza budżetu
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Budżet miesięczny
                  </Typography>
                  
                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Wykorzystane</Typography>
                      <Typography variant="body2">
                        {analyticsData.budgetData.spent.toLocaleString()} / {analyticsData.budgetData.monthly.toLocaleString()} zł
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={budgetPercentage}
                      sx={{ height: 8, borderRadius: 1 }}
                      color={budgetPercentage > 90 ? 'error' : budgetPercentage > 80 ? 'warning' : 'primary'}
                    />
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Pozostało</Typography>
                      <Typography variant="h6" color="success.main">
                        {analyticsData.budgetData.remaining.toLocaleString()} zł
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Prognoza</Typography>
                      <Typography variant="h6" color={analyticsData.budgetData.projection > analyticsData.budgetData.monthly ? 'error.main' : 'primary.main'}>
                        {analyticsData.budgetData.projection.toLocaleString()} zł
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Rekomendacje
                  </Typography>
                  
                  <Box mb={2}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Budżet może zostać przekroczony o ~200 zł
                    </Alert>
                    <Alert severity="info">
                      Rozważ opóźnienie zamówień niepriorytedowych
                    </Alert>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Sugerowane działania:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                    <Typography component="li" variant="body2">
                      Przełóż zamówienie tonerów na przyszły miesiąc
                    </Typography>
                    <Typography component="li" variant="body2">
                      Wykorzystaj rabaty ilościowe na papier
                    </Typography>
                    <Typography component="li" variant="body2">
                      Sprawdź promocje BiuroPaczka
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          {/* Savings */}
          <Typography variant="h6" gutterBottom>
            Oszczędności w tym roku
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center">
                        <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 1 }}>
                          <MoneyIcon />
                        </Avatar>
                        <Typography variant="h6" color="success.main">
                          {analyticsData.savings.bulkDiscounts} zł
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Rabaty ilościowe
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center">
                        <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 1 }}>
                          <TrendingUpIcon />
                        </Avatar>
                        <Typography variant="h6" color="info.main">
                          {analyticsData.savings.loyaltyProgram} zł
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Program lojalnościowy
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center">
                        <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 1 }}>
                          <AssessmentIcon />
                        </Avatar>
                        <Typography variant="h6" color="warning.main">
                          {analyticsData.savings.promotions} zł
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Promocje specjalne
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} md={3}>
                      <Box textAlign="center">
                        <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 1 }}>
                          <MoneyIcon />
                        </Avatar>
                        <Typography variant="h6" color="primary.main">
                          {analyticsData.savings.total} zł
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Łącznie
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Możliwości oszczędności
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Potencjalne dodatkowe oszczędności:
                  </Typography>
                  
                  <Box mb={2}>
                    <Typography variant="body2" fontWeight="medium">
                      Zamówienia automatyczne: +150 zł/rok
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rabat za regularne dostawy
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" fontWeight="medium">
                      Ekologiczne alternatywy: +200 zł/rok
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ulga podatkowa + rabaty
                    </Typography>
                  </Box>
                  
                  <Button variant="outlined" fullWidth size="small">
                    Więcej informacji
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>
    </Box>
  );
}